// frontend/src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import { LabProvider } from './contexts/LabContext';
import LabViewer from './components/lab/LabViewer';
import LogsPage from './pages/LogsPage';
import PublicExamHomeScreen from './pages/PublicExamHomeScreen';
import CheckoutReturnScreen from './pages/CheckoutReturnScreen';
import PaymentDashboardPage from './pages/PaymentDashboardPage';
import ProgressDashboardPage from './pages/ProgressDashboardPage';
import SmartExamRouter from './components/smart/SmartExamRouter';
import ProtectedRoute from './components/auth/ProtectedRoute';
import UserMenuControl from './components/layout/UserMenuControl';
import AppNavBrand from './components/layout/AppNavBrand';
import AppFooter from './components/layout/AppFooter';
import AuthModal from './components/auth/AuthModal';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ADAPTIVE_EXAM_ENTRY_KEY } from './constants/adaptiveEntry';
import { readGuestQcmSession, clearGuestQcmSession } from './utils/guestSession';

import './App.css';
import './index.css';
import './i18n/config';

function readInitialAppPage() {
  try {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success' || checkout === 'cancel') {
      return 'checkout_return';
    }
    const tok = localStorage.getItem('auth_token');
    if (!tok) {
      const g = readGuestQcmSession();
      if (g && g.examId && localStorage.getItem(ADAPTIVE_EXAM_ENTRY_KEY) !== 'picker') {
        return 'exam';
      }
      return 'public_home';
    }
    return localStorage.getItem(ADAPTIVE_EXAM_ENTRY_KEY) === 'picker' ? 'exam_hub' : 'exam';
  } catch {
    return 'public_home';
  }
}

function GuestExamRedirect({ onGoHome }) {
  useEffect(() => {
    onGoHome();
  }, [onGoHome]);
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#64748b',
      }}
    >
      <LoadingSpinner size="large" />
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const { user, isGuest, isAuthenticated, loading: authLoading } = useAuth();
  // Le visiteur public est traité comme « pas d'utilisateur » côté navigation :
  // on lui montre le catalogue libre + les examens extract_free, mais jamais la
  // mire d'authentification automatiquement. Celle-ci ne s'ouvre que lorsqu'il
  // clique explicitement sur Sign in (handleOpenLoginModal) ou tente d'accéder
  // à une zone réservée (lab/logs) où on lui propose le bouton de connexion.
  const isPublicVisitor = !user || isGuest;
  const [currentPage, setCurrentPage] = useState(() => readInitialAppPage());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const checkoutSessionId = (() => {
    try {
      return new URLSearchParams(window.location.search).get('session_id') || '';
    } catch {
      return '';
    }
  })();
  const checkoutStatus = (() => {
    try {
      return new URLSearchParams(window.location.search).get('checkout') || '';
    } catch {
      return '';
    }
  })();
  const prevAuthedRef = useRef(isAuthenticated);

  const goPublicHome = useCallback(() => {
    clearGuestQcmSession();
    try {
      localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
    } catch {
      /* ignore */
    }
    setCurrentPage('public_home');
  }, []);

  const handleUserNavigate = useCallback(
    (target) => {
      if (target === 'catalog') {
        try {
          localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
        } catch {
          /* ignore */
        }
        if (isPublicVisitor) {
          clearGuestQcmSession();
          setCurrentPage('public_home');
        } else {
          setCurrentPage('exam_hub');
        }
        return;
      }
      if (target === 'practice') {
        const hasExam =
          !isPublicVisitor && user && String(user.lastSelections?.examId || '').trim();
        if (hasExam) {
          try {
            localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'practice');
          } catch {
            /* ignore */
          }
          setCurrentPage('exam');
        }
        return;
      }
      if (target === 'lab') {
        setCurrentPage('lab');
        return;
      }
      if (target === 'logs') {
        setCurrentPage('logs');
        return;
      }
      if (target === 'payment_dashboard') {
        setCurrentPage('payment_dashboard');
        return;
      }
      if (target === 'progress_dashboard') {
        setCurrentPage('progress_dashboard');
      }
    },
    [user, isPublicVisitor]
  );

  useEffect(() => {
    const onNav = (e) => {
      if (e?.detail?.page === 'home') {
        clearGuestQcmSession();
        try {
          localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
        } catch {
          /* ignore */
        }
        if (isPublicVisitor) {
          setCurrentPage('public_home');
        } else {
          setCurrentPage('exam_hub');
        }
      }
    };
    window.addEventListener('nav_request', onNav);
    return () => window.removeEventListener('nav_request', onNav);
  }, [isPublicVisitor]);

  /**
   * Ouverture explicite du modal d'auth.
   * Ne JAMAIS appeler ce handler depuis la logique de navigation : la mire
   * d'authentification ne doit s'ouvrir que sur action volontaire de l'utilisateur
   * (clic sur Sign in dans la barre, ou bouton « Sign in to continue » d'une zone
   * réservée comme Lab/Logs).
   */
  const handleOpenLoginModal = useCallback(() => setShowAuthModal(true), []);

  useEffect(() => {
    const onRequestLogin = () => setShowAuthModal(true);
    window.addEventListener('request_open_login_modal', onRequestLogin);
    return () => window.removeEventListener('request_open_login_modal', onRequestLogin);
  }, []);

  useEffect(() => {
    const onLogin = () => {
      setShowAuthModal(false);
      setCurrentPage('exam_hub');
    };
    window.addEventListener('auth_logged_in', onLogin);
    return () => window.removeEventListener('auth_logged_in', onLogin);
  }, []);

  useEffect(() => {
    const onLogoutEvt = () => {
      setShowAuthModal(false);
      setCurrentPage('public_home');
    };
    window.addEventListener('auth_logged_out', onLogoutEvt);
    return () => window.removeEventListener('auth_logged_out', onLogoutEvt);
  }, []);

  useEffect(() => {
    const was = prevAuthedRef.current;
    prevAuthedRef.current = isAuthenticated;
    if (was && !isAuthenticated) {
      setCurrentPage('public_home');
      try {
        localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
      } catch {
        /* ignore */
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (isGuest) {
      const guestExam = String(readGuestQcmSession()?.examId || '').trim();
      if (!guestExam && currentPage === 'exam') {
        try {
          localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
        } catch {
          /* ignore */
        }
        setCurrentPage('public_home');
      }
      return;
    }
    const hasExam = String(user.lastSelections?.examId || '').trim();
    if (!hasExam && currentPage === 'exam') {
      try {
        localStorage.setItem(ADAPTIVE_EXAM_ENTRY_KEY, 'picker');
      } catch {
        /* ignore */
      }
      setCurrentPage('exam_hub');
    }
  }, [authLoading, user, isGuest, currentPage]);

  const canResumePractice = !!(
    !isPublicVisitor && user && String(user.lastSelections?.examId || '').trim()
  );
  const showFooter =
    currentPage === 'public_home' ||
    currentPage === 'exam_hub' ||
    currentPage === 'checkout_return' ||
    showAuthModal;

  const renderPage = () => {
    switch (currentPage) {
      case 'public_home':
        return (
          <PublicExamHomeScreen
            mode="guest"
            onNeedLogin={handleOpenLoginModal}
            onPractice={() => {
              setCurrentPage('exam');
            }}
          />
        );
      case 'exam': {
        if (authLoading && localStorage.getItem('auth_token')) {
          return (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#111827',
              }}
            >
              <LoadingSpinner size="large" />
            </div>
          );
        }
        // Guest (auto-loginé) ou user réel : tous deux peuvent ouvrir un examen.
        // Le backend filtre déjà au scope free pour le guest, et renvoie 403
        // si l'examen n'est pas extract_free → pas besoin de bloquer ici.
        if (user) {
          return <SmartExamRouter />;
        }
        if (readGuestQcmSession()?.examId) {
          return <SmartExamRouter />;
        }
        return <GuestExamRedirect onGoHome={goPublicHome} />;
      }
      case 'exam_hub': {
        // Hub réservé aux comptes réels (catalogue habilité). Les guests sont
        // renvoyés en silence sur le catalogue public — pas de mire d'auth.
        if (isPublicVisitor) {
          return (
            <PublicExamHomeScreen
              mode="guest"
              onNeedLogin={handleOpenLoginModal}
              onPractice={() => setCurrentPage('exam')}
            />
          );
        }
        return (
          <ProtectedRoute>
            <PublicExamHomeScreen
              mode="auth"
              onNeedLogin={handleOpenLoginModal}
              onPractice={() => setCurrentPage('exam')}
            />
          </ProtectedRoute>
        );
      }
      case 'checkout_return':
        return (
          <CheckoutReturnScreen
            status={checkoutStatus === 'cancel' ? 'cancel' : 'success'}
            sessionId={checkoutSessionId}
            onNeedLogin={handleOpenLoginModal}
            onDone={() => {
              if (isPublicVisitor) {
                setCurrentPage('public_home');
              } else {
                setCurrentPage('exam_hub');
              }
            }}
          />
        );
      case 'lab':
        return (
          <ProtectedRoute>
            <LabProvider>
              <LabViewer />
            </LabProvider>
          </ProtectedRoute>
        );
      case 'logs':
        return (
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        );
      case 'payment_dashboard':
        return (
          <ProtectedRoute>
            <PaymentDashboardPage />
          </ProtectedRoute>
        );
      case 'progress_dashboard':
        return (
          <ProtectedRoute>
            <ProgressDashboardPage />
          </ProtectedRoute>
        );
      default:
        if (user) return <SmartExamRouter />;
        return <GuestExamRedirect onGoHome={goPublicHome} />;
    }
  };

  if (authLoading && localStorage.getItem('auth_token')) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#111827',
          color: '#93c5fd',
        }}
      >
        <LoadingSpinner size="large" />
        <p style={{ marginTop: 12, fontSize: 14 }}>{t('session_loading')}</p>
      </div>
    );
  }

  return (
    <div style={styles.appContainer}>
      <nav style={styles.navbar}>
        <AppNavBrand />
        <div style={{ flex: 1 }} />
        <UserMenuControl
          onOpenLogin={handleOpenLoginModal}
          onNavigate={handleUserNavigate}
          canResumePractice={canResumePractice}
        />
      </nav>

      <div style={styles.mainContent}>{renderPage()}</div>

      {showFooter && <AppFooter />}

      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}

const styles = {
  appContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  navbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    backgroundColor: '#2c3e50',
    color: 'white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    zIndex: 1000,
    gap: 12,
  },
  mainContent: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
};

export default App;
